/**
 * Control-plane WebSocket client for the Noki Copilot.
 *
 * Runs alongside the WebRTC audio plane, pushing structured context
 * updates + action registrations to the copilot server, and surfacing
 * server-issued tool dispatches and goal events as React-visible events.
 *
 * Owns reconnection with exponential backoff. The consuming hook
 * observes status and feeds context / actions / goal-button presses in
 * — the socket itself never decides anything, just routes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getCopilotAuthToken } from "./authToken";
import { CONTROL_PROTOCOL_VERSION, isServerMessage } from "./protocol";

const WIDGET_VERSION = "0.1.0-noki-fe";

const RECONNECT_BASE_MS = 750;
const RECONNECT_MAX_MS = 15_000;
const PING_INTERVAL_MS = 25_000;
// Trailing-debounce window for context.update pushes — collapses the burst of
// register/unregister notifications a single navigation emits into one frame.
const PUSH_DEBOUNCE_MS = 80;

const buildWsUrl = (serverUrl) => {
  const base = serverUrl || window.location.origin;
  const url = new URL("/ws/copilot", base);
  if (url.protocol === "http:") url.protocol = "ws:";
  else if (url.protocol === "https:") url.protocol = "wss:";
  return url.toString();
};

/**
 * Translate the noki-fe-side action descriptor (camelCase, JS conventions)
 * into the server's wire shape (snake_case, pydantic-validated). Without
 * this the server's `extra=forbid` rejects unknown camelCase fields.
 */
const actionSpecToWire = (action) => ({
  name: action.name,
  description: action.description,
  classification: action.classification,
  parameters: (action.parameters ?? []).map((p) => ({
    name: p.name,
    type: p.type,
    description: p.description,
    required: p.required ?? true,
    enum: p.enum ?? null,
  })),
  confirm_style: action.confirmStyle ?? "none",
});

/**
 * @param {object}   options
 * @param {string}   options.serverUrl
 * @param {string}   options.sessionId
 * @param {boolean}  [options.enabled]
 * @param {(d: object) => void} options.onToolDispatch
 * @param {(e: object) => void} [options.onGoalEvent]
 * @param {() => void} [options.onSessionClosed] Fired when the server
 *        reports our session_id is dead (code === "session_closed").
 *        The caller is expected to rotate the sessionStorage id so the
 *        next reconnect uses a fresh one.
 */
export function useControlSocket(options) {
  const {
    serverUrl,
    sessionId,
    enabled = true,
    onToolDispatch,
    onGoalEvent,
    onSessionClosed,
    onBudgetNotice,
  } = options;

  const [status, setStatus] = useState("idle");
  const [serverVersion, setServerVersion] = useState(null);
  const [lastError, setLastError] = useState(null);
  // pair_token is the server-issued HMAC-signed credential we MUST
  // forward in every /api/offer POST. The server mints a fresh one
  // for every WS handshake (session.ready frame). Stored in state so
  // useVoiceClient (the offer caller) can pull it via a callback when
  // the user clicks "mic". Cleared on disconnect so a stale token from
  // a previous WS doesn't get reused after reconnect.
  const [pairToken, setPairToken] = useState(null);

  const socketRef = useRef(null);
  const seqRef = useRef(0);
  const contextQueueRef = useRef([]);
  const sidebandQueueRef = useRef([]);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  // Trailing-debounce a burst of context.update pushes (a single navigation
  // fires several register/unregister notifications, intermediate ones stale)
  // into one final snapshot. See pushContext.
  const pushTimerRef = useRef(null);
  const pendingContextRef = useRef(null);
  const closedByCallerRef = useRef(false);

  const onToolDispatchRef = useRef(onToolDispatch);
  onToolDispatchRef.current = onToolDispatch;
  const onGoalEventRef = useRef(onGoalEvent);
  onGoalEventRef.current = onGoalEvent;
  const onSessionClosedRef = useRef(onSessionClosed);
  onSessionClosedRef.current = onSessionClosed;
  const onBudgetNoticeRef = useRef(onBudgetNotice);
  onBudgetNoticeRef.current = onBudgetNotice;

  const wsUrl = useMemo(() => buildWsUrl(serverUrl), [serverUrl]);

  const sendRaw = useCallback((socket, payload) => {
    if (socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify(payload));
  }, []);

  const sendContextUpdate = useCallback(
    (socket, context) => {
      seqRef.current += 1;
      sendRaw(socket, {
        type: "context.update",
        protocol_version: CONTROL_PROTOCOL_VERSION,
        session_id: sessionId,
        seq: seqRef.current,
        context,
      });
    },
    [sendRaw, sessionId],
  );

  const sendSideband = useCallback(
    (socket, item) => {
      switch (item.kind) {
        case "register":
          sendRaw(socket, {
            type: "actions.register",
            protocol_version: CONTROL_PROTOCOL_VERSION,
            session_id: sessionId,
            page_id: item.pageId,
            version: item.version,
            tools: item.tools.map(actionSpecToWire),
          });
          break;
        case "unregister":
          sendRaw(socket, {
            type: "actions.unregister",
            protocol_version: CONTROL_PROTOCOL_VERSION,
            session_id: sessionId,
            page_id: item.pageId,
          });
          break;
        case "result":
          sendRaw(socket, {
            type: "tool.result",
            protocol_version: CONTROL_PROTOCOL_VERSION,
            session_id: sessionId,
            call_id: item.payload.callId,
            status: item.payload.status,
            data: item.payload.data ?? null,
            error_code: item.payload.errorCode ?? null,
            error_message: item.payload.errorMessage ?? null,
          });
          break;
        case "action_status":
          sendRaw(socket, {
            type: "action.status",
            protocol_version: CONTROL_PROTOCOL_VERSION,
            session_id: sessionId,
            origin: item.payload.origin,
            phase: item.payload.phase,
            action: item.payload.action,
            call_id: item.payload.callId ?? null,
            node: item.payload.node ?? null,
            error_code: item.payload.errorCode ?? null,
          });
          break;
        case "goal":
          sendRaw(socket, {
            type: item.intent === "confirm" ? "goal.confirm" : "goal.cancel",
            protocol_version: CONTROL_PROTOCOL_VERSION,
            session_id: sessionId,
            goal_id: item.goalId,
          });
          break;
        case "copilot_pause":
          sendRaw(socket, {
            type: "copilot.pause",
            protocol_version: CONTROL_PROTOCOL_VERSION,
            session_id: sessionId,
            paused: item.paused,
          });
          break;
        default:
          break;
      }
    },
    [sendRaw, sessionId],
  );

  const flushQueues = useCallback(
    (socket) => {
      while (contextQueueRef.current.length > 0) {
        const next = contextQueueRef.current.shift();
        if (!next) break;
        sendContextUpdate(socket, next);
      }
      while (sidebandQueueRef.current.length > 0) {
        const next = sidebandQueueRef.current.shift();
        if (!next) break;
        sendSideband(socket, next);
      }
    },
    [sendContextUpdate, sendSideband],
  );

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current !== null) {
      window.clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
    if (pushTimerRef.current !== null) {
      window.clearTimeout(pushTimerRef.current);
      pushTimerRef.current = null;
    }
  }, []);

  const openSocketRef = useRef(null);

  const scheduleReconnect = useCallback(() => {
    if (closedByCallerRef.current) return;
    const attempt = reconnectAttemptsRef.current + 1;
    reconnectAttemptsRef.current = attempt;
    const delay = Math.min(
      RECONNECT_BASE_MS * 2 ** (attempt - 1),
      RECONNECT_MAX_MS,
    );
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      const s = socketRef.current;
      if (s && s.readyState !== WebSocket.CLOSED) s.close();
      openSocketRef.current?.();
    }, delay);
  }, []);

  const handleMessage = useCallback(
    (event) => {
      let parsed;
      try {
        parsed = JSON.parse(event.data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[copilot] ws.frame.malformed", {
          error: err?.message ?? String(err),
          preview: typeof event.data === "string" ? event.data.slice(0, 120) : "?",
        });
        setLastError("malformed frame from server");
        return;
      }
      if (!isServerMessage(parsed)) {
        // eslint-disable-next-line no-console
        console.warn("[copilot] ws.frame.unrecognised", {
          type: parsed?.type ?? "unknown",
        });
        setLastError("unrecognised server message");
        return;
      }
      const message = parsed;
      // eslint-disable-next-line no-console
      console.info("[copilot] ws.message", { type: message.type });
      if (message.type === "session.ready") {
        setServerVersion(message.server_version);
        setStatus("ready");
        setLastError(null);
        reconnectAttemptsRef.current = 0;
        // Capture the freshly-minted pair token so useVoiceClient can
        // forward it on the next /api/offer POST. The server only
        // accepts the token from THIS WS session; without it the offer
        // is 401'd. A new WS handshake (reconnect, new tab, refresh)
        // mints a new token — never reuse one from a prior session.
        if (typeof message.pair_token === "string" && message.pair_token) {
          setPairToken(message.pair_token);
        } else {
          // eslint-disable-next-line no-console
          console.error("[copilot] ws.session.ready.missing_pair_token", {
            detail: (
              "Server did not include pair_token. Voice will fail to "
              + "connect — check that the server is at least pair-token "
              + "v4 (PROTOCOL_VERSION === '4')."
            ),
          });
          setPairToken(null);
        }
        const socket = socketRef.current;
        if (socket && socket.readyState === WebSocket.OPEN) flushQueues(socket);
      } else if (message.type === "pong") {
        // round-trip itself is the keepalive.
        //
        // Server v4+ also rotates the pair token on every pong (see
        // ws.py ping handler) so a long-idle panel always holds a
        // credential under one ping interval old, AND a second
        // voice click on the same WS gets a fresh jti rather than
        // re-using the consumed token from session.ready. Capture
        // the refreshed token if present; otherwise leave the
        // existing one in place (graceful with older servers).
        if (typeof message.pair_token === "string" && message.pair_token) {
          setPairToken(message.pair_token);
        }
      } else if (message.type === "error") {
        // eslint-disable-next-line no-console
        console.error("[copilot] ws.server_error", {
          code: message.code,
          detail: message.message,
        });
        setLastError(`${message.code}: ${message.message}`);
        setStatus("error");
        // The server is the source of truth for session lifecycle: when
        // it tells us our id is dead, drop it so the caller can mint a
        // fresh one. Without this the widget would loop forever sending
        // session.init with the same rejected id.
        //
        // Server may use either `session_closed` (explicit X-button or
        // logout) or `session_expired` (TTL sweeper) — both mean the
        // same thing to the widget: clear sessionStorage and reconnect
        // with a fresh id.
        if (
          message.code === "session_closed"
          || message.code === "session_expired"
        ) {
          try {
            onSessionClosedRef.current?.();
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error("[copilot] ws.session_closed.handler_error", {
              code: message.code,
              error: err?.message ?? String(err),
            });
          }
        }
      } else if (message.type === "tool.dispatch") {
        // Defense-in-depth (pair-token v4): every server-emitted
        // dispatch carries the session_id it was routed to. If that
        // doesn't match THIS panel's sessionId, the server either
        // misrouted or someone is replaying a captured frame onto our
        // socket. Either way: refuse to execute the action and tell
        // the server it landed on the wrong client. The server's
        // logs already flag this as cross-routing — we just make sure
        // the wrong browser never actually runs the host action.
        if (
          typeof message.session_id === "string"
          && message.session_id !== sessionId
        ) {
          // eslint-disable-next-line no-console
          console.error("[copilot] ws.tool_dispatch.session_mismatch", {
            ours: sessionId,
            dispatch_session_id: message.session_id,
            call_id: message.call_id,
            tool_name: message.tool_name,
            detail: (
              "Refusing to execute — a tool.dispatch arrived on this "
              + "WebSocket with a session_id that does not belong to "
              + "this panel. This indicates a server-side routing "
              + "regression. The wrong-target action is BLOCKED."
            ),
          });
          const socket = socketRef.current;
          if (socket && socket.readyState === WebSocket.OPEN) {
            sendRaw(socket, {
              type: "tool.result",
              protocol_version: CONTROL_PROTOCOL_VERSION,
              // We MUST report back with our OWN session_id so the
              // server can match the result to the (broken) dispatch.
              session_id: sessionId,
              call_id: message.call_id,
              status: "error",
              error_code: "session_mismatch",
              error_message: (
                "Dispatch session_id did not match panel sessionId; "
                + "the dispatch was routed to the wrong client and "
                + "was refused without execution."
              ),
            });
          }
          return;
        }
        try {
          onToolDispatchRef.current?.(message);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[copilot] ws.tool_dispatch.handler_error", {
            error: err?.message ?? String(err),
          });
        }
      } else if (
        message.type === "goal.update" ||
        message.type === "goal.commit" ||
        message.type === "goal.dismiss"
      ) {
        // Same defense-in-depth as tool.dispatch — goal frames already
        // carry session_id (have since v3), but we hadn't been checking
        // it. Mismatched goal frames are silently dropped: there's no
        // ack channel to report back on, but the panel must not render
        // someone else's workflow card.
        if (
          typeof message.session_id === "string"
          && message.session_id !== sessionId
        ) {
          // eslint-disable-next-line no-console
          console.error("[copilot] ws.goal_event.session_mismatch", {
            type: message.type,
            ours: sessionId,
            event_session_id: message.session_id,
            goal_id: message.goal_id,
            detail: "Goal event for another session — dropping.",
          });
          return;
        }
        try {
          onGoalEventRef.current?.(message);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[copilot] ws.goal_event.handler_error", {
            type: message.type,
            error: err?.message ?? String(err),
          });
        }
      } else if (message.type === "budget.notice") {
        // Governance banner (80% heads-up or final wind-down reason). Same
        // wrong-target defense-in-depth as tool.dispatch: a notice for a
        // different session_id must never render on this panel.
        if (
          typeof message.session_id === "string"
          && message.session_id !== sessionId
        ) {
          // eslint-disable-next-line no-console
          console.error("[copilot] ws.budget_notice.session_mismatch", {
            ours: sessionId,
            notice_session_id: message.session_id,
            kind: message.kind,
            level: message.level,
          });
          return;
        }
        try {
          onBudgetNoticeRef.current?.(message);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[copilot] ws.budget_notice.handler_error", {
            kind: message.kind,
            level: message.level,
            error: err?.message ?? String(err),
          });
        }
      }
    },
    // sessionId + sendRaw are read inside the session_mismatch
    // refusal path; including them so a future sessionId rotation
    // recreates the closure with the fresh value.
    [flushQueues, sessionId, sendRaw],
  );

  useEffect(() => {
    if (!enabled) {
      setStatus("idle");
      return undefined;
    }
    closedByCallerRef.current = false;

    const open = () => {
      if (
        socketRef.current &&
        socketRef.current.readyState !== WebSocket.CLOSED
      ) {
        return;
      }
      setStatus("connecting");
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.addEventListener("open", async () => {
        // eslint-disable-next-line no-console
        console.info("[copilot] ws.open", { url: wsUrl, sessionId });
        setStatus("open");
        // Fetch a fresh-enough Firebase ID token BEFORE sending
        // session.init. Browsers can't set headers on WebSocket
        // connections, so the token rides inside the init message.
        // If the socket got closed during the await (e.g. user
        // navigated away while the token was resolving), bail out
        // before sending — the close handler will trigger reconnect.
        const authToken = await getCopilotAuthToken();
        if (socket.readyState !== WebSocket.OPEN) return;
        sendRaw(socket, {
          type: "session.init",
          protocol_version: CONTROL_PROTOCOL_VERSION,
          session_id: sessionId,
          widget_version: WIDGET_VERSION,
          auth_token: authToken,
        });
        if (pingTimerRef.current !== null) {
          window.clearInterval(pingTimerRef.current);
        }
        pingTimerRef.current = window.setInterval(() => {
          sendRaw(socket, {
            type: "ping",
            protocol_version: CONTROL_PROTOCOL_VERSION,
          });
        }, PING_INTERVAL_MS);
      });

      socket.addEventListener("message", handleMessage);

      socket.addEventListener("close", (event) => {
        if (pingTimerRef.current !== null) {
          window.clearInterval(pingTimerRef.current);
          pingTimerRef.current = null;
        }
        // eslint-disable-next-line no-console
        console.info("[copilot] ws.close", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          intentional: closedByCallerRef.current,
        });
        // Clear the pair token — the server revokes it server-side on
        // WS close (see ws.py finally block), and it can't be redeemed
        // anyway after reconnect mints a fresh one. Holding a stale
        // value in state would be a footgun for useVoiceClient if a
        // click landed in the gap between close and the next
        // session.ready.
        setPairToken(null);
        if (closedByCallerRef.current) {
          setStatus("closed");
          return;
        }
        setStatus("closed");
        scheduleReconnect();
      });

      socket.addEventListener("error", (event) => {
        // eslint-disable-next-line no-console
        console.error("[copilot] ws.error", {
          message: event?.message ?? "websocket error",
        });
        setLastError("websocket error");
      });
    };

    openSocketRef.current = open;
    open();

    return () => {
      closedByCallerRef.current = true;
      clearTimers();
      const socket = socketRef.current;
      socketRef.current = null;
      // Intentional NOT to send session.end here — useEffect cleanups
      // may also run on F5/page-unload, which would defeat the lenient
      // reconnect rule. session.end is emitted only from explicit user
      // actions (X-button via sendSessionEnd below). Tab-close /
      // network drop falls through to the server-side TTL sweeper.
      if (socket) socket.close();
      openSocketRef.current = null;
    };
  }, [
    enabled,
    wsUrl,
    sessionId,
    sendRaw,
    handleMessage,
    scheduleReconnect,
    clearTimers,
  ]);

  const isReady = (s) => status === "ready" && s && s.readyState === WebSocket.OPEN;

  const pushContext = useCallback(
    (context) => {
      pendingContextRef.current = context;
      const socket = socketRef.current;
      if (!isReady(socket)) {
        // Coalesce: keep only the latest snapshot when the socket is not ready
        // (flushed on `ready`). No timer needed — flushQueues drains it.
        contextQueueRef.current = [context];
        return;
      }
      // Ready: trailing-debounce so a burst of register/unregister
      // notifications during ONE navigation collapses to the final snapshot
      // (prevents the stale intermediate page_id frames / oscillation).
      if (pushTimerRef.current !== null) return; // trailing send already scheduled
      pushTimerRef.current = window.setTimeout(() => {
        pushTimerRef.current = null;
        const ctx = pendingContextRef.current;
        pendingContextRef.current = null;
        if (!ctx) return;
        const s = socketRef.current;
        if (isReady(s)) sendContextUpdate(s, ctx);
        else contextQueueRef.current = [ctx]; // dropped to ready → flushes later
      }, PUSH_DEBOUNCE_MS);
    },
    // `status` is read inside isReady — include it so callbacks update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sendContextUpdate, status],
  );

  const registerActions = useCallback(
    (input) => {
      const socket = socketRef.current;
      const payload = {
        kind: "register",
        pageId: input.pageId,
        version: input.version,
        tools: input.tools,
      };
      if (isReady(socket)) {
        sendSideband(socket, payload);
        return;
      }
      // Drop any earlier register for the same page so we don't double-dispatch.
      sidebandQueueRef.current = sidebandQueueRef.current.filter(
        (item) => !(item.kind === "register" && item.pageId === input.pageId),
      );
      sidebandQueueRef.current.push(payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sendSideband, status],
  );

  const unregisterActions = useCallback(
    (pageId) => {
      const socket = socketRef.current;
      const payload = { kind: "unregister", pageId };
      if (isReady(socket)) {
        sendSideband(socket, payload);
        return;
      }
      sidebandQueueRef.current = sidebandQueueRef.current.filter(
        (item) => !(item.kind === "register" && item.pageId === pageId),
      );
      sidebandQueueRef.current.push(payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sendSideband, status],
  );

  const sendToolResult = useCallback(
    (result) => {
      const socket = socketRef.current;
      const payload = { kind: "result", payload: result };
      if (isReady(socket)) {
        sendSideband(socket, payload);
        return;
      }
      sidebandQueueRef.current.push(payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sendSideband, status],
  );

  // Phase-2b narration-sync: report an action's execution lifecycle
  // (started/completed/failed, agent OR manual) so the copilot narrates in
  // sync with what actually happened. Same sideband channel + queue-when-not-
  // ready semantics as sendToolResult.
  const sendActionStatus = useCallback(
    (payload) => {
      const socket = socketRef.current;
      const item = { kind: "action_status", payload };
      if (isReady(socket)) {
        sendSideband(socket, item);
        return;
      }
      sidebandQueueRef.current.push(item);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sendSideband, status],
  );

  const sendGoal = useCallback(
    (goalId, intent) => {
      const socket = socketRef.current;
      const payload = { kind: "goal", goalId, intent };
      if (isReady(socket)) {
        sendSideband(socket, payload);
        return;
      }
      // Last click wins — replace any pending intent for this goal so a
      // stale confirm can't override a later cancel.
      sidebandQueueRef.current = sidebandQueueRef.current.filter(
        (item) => !(item.kind === "goal" && item.goalId === goalId),
      );
      sidebandQueueRef.current.push(payload);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sendSideband, status],
  );

  // Tell the server the copilot is externally paused/resumed (manual pause or
  // recording lock) so its idle watchdog freezes the silence clock instead of
  // stopping a session the clinician intends to resume. Same queue-when-not-
  // ready semantics as the other sideband sends; last-write-wins so a rapid
  // pause/resume can't leave a stale value queued.
  const sendCopilotPause = useCallback(
    (paused) => {
      const socket = socketRef.current;
      const item = { kind: "copilot_pause", paused: !!paused };
      if (isReady(socket)) {
        sendSideband(socket, item);
        return;
      }
      sidebandQueueRef.current = sidebandQueueRef.current.filter(
        (queued) => queued.kind !== "copilot_pause",
      );
      sidebandQueueRef.current.push(item);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sendSideband, status],
  );

  const sendGoalConfirm = useCallback(
    (goalId) => sendGoal(goalId, "confirm"),
    [sendGoal],
  );
  const sendGoalCancel = useCallback(
    (goalId) => sendGoal(goalId, "cancel"),
    [sendGoal],
  );

  // Send an explicit session.end frame. Caller decides the reason —
  // typically "user_close" (X button clicked) or "logout". The server
  // flips the durable session row to status='closed' so subsequent
  // session.init attempts with the same id are rejected. Best-effort:
  // if the socket isn't open, we drop silently — by the time the
  // server's TTL sweeper runs, the row will expire anyway.
  const sendSessionEnd = useCallback(
    (reason) => {
      const socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      try {
        socket.send(
          JSON.stringify({
            type: "session.end",
            protocol_version: CONTROL_PROTOCOL_VERSION,
            session_id: sessionId,
            reason,
          }),
        );
      } catch {
        // Socket may have torn down between the readyState check and
        // the send. Nothing actionable; TTL sweeper picks it up later.
      }
    },
    [sessionId],
  );

  return {
    status,
    serverVersion,
    lastError,
    // Pair token minted by the server during the WS handshake. The
    // /api/offer caller MUST include this in its POST body or the
    // server returns 401. null until session.ready arrives; cleared
    // on disconnect. See ws.py + pair_broker.py on the server side.
    pairToken,
    pushContext,
    registerActions,
    unregisterActions,
    sendToolResult,
    sendActionStatus,
    sendCopilotPause,
    sendGoalConfirm,
    sendGoalCancel,
    sendSessionEnd,
  };
}
